import React from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface WordmarkProps {
    /**
     * Color of the "ungloss" base. Defaults to `'inherit'` so the base adopts
     * the surrounding text color (e.g. inside a heading). The nav logo passes
     * `'text.primary'`.
     */
    baseColor?: string
    /**
     * Font weight of the "ungloss" base. Defaults to inherit so it matches the
     * surrounding text. The nav logo passes `500` per the brand spec.
     */
    baseFontWeight?: number
}

/**
 * The ungloss.io wordmark: the brand name with its ".io" suffix rendered in a
 * lighter, true-italic coral weight per the brand spec. The name is sourced
 * from the `navigation:transliterate` i18n value so it lives in one place, and
 * is split on the last dot into base + italic suffix. When there is no dot
 * (e.g. the dotless i18n key in key-mode tests) the whole string renders as the
 * base. This is the single source of truth for the styled wordmark — use it
 * anywhere the product name should carry the brand treatment.
 */
export const Wordmark: React.FC<WordmarkProps> = ({ baseColor = 'inherit', baseFontWeight }) => {
    const { t } = useTranslation()
    const wordmark = t('navigation:transliterate')
    const dotIndex = wordmark.lastIndexOf('.')
    const base = dotIndex > 0 ? wordmark.slice(0, dotIndex) : wordmark
    const suffix = dotIndex > 0 ? wordmark.slice(dotIndex) : ''

    return (
        <Box
            component="span"
            aria-label={wordmark}
            sx={{ display: 'inline-flex', alignItems: 'baseline' }}
        >
            <Box component="span" sx={{ color: baseColor, fontWeight: baseFontWeight }}>
                {base}
            </Box>
            {suffix && (
                <Box
                    component="span"
                    sx={{ color: 'brand.primary', fontWeight: 400, fontStyle: 'italic' }}
                >
                    {suffix}
                </Box>
            )}
        </Box>
    )
}
